import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { openCalendly } from "@/lib/calendly";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_drink-food-expert/artifacts/dl9rwqw0_high-resolution-color-logo.png";

const navLinks = [
    { label: "Services", href: "#services" },
    { label: "Packages", href: "#packages" },
    { label: "Process", href: "#process" },
    { label: "About", href: "#about" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-brand-cream/95 backdrop-blur-md border-b border-brand-border-light"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={LOGO_URL}
                        alt="Eat + Drink Consulting"
                        width="48"
                        height="48"
                        loading="eager"
                        decoding="async"
                        className="h-12 w-12 rounded-full object-cover"
                    />
                    <span className="hidden sm:flex flex-col leading-none">
                        <span className="font-serif text-xl text-brand-dark font-medium tracking-tight">
                            Eat <span className="text-brand-orange">+</span> Drink
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-brand-muted mt-1">
                            Consulting
                        </span>
                    </span>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {navLinks.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="nav-link text-sm uppercase tracking-[0.18em] text-brand-dark hover:text-brand-orange transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <button
                    type="button"
                    onClick={() => openCalendly("30min", "header-book-call")}
                    data-testid="header-cta-button"
                    className="hidden md:inline-flex items-center gap-2 bg-brand-orange text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-brand-orange-hover transition-colors cursor-pointer"
                >
                    Book a Call
                </button>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="md:hidden text-brand-dark"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="md:hidden overflow-hidden bg-brand-cream border-t border-brand-border-light"
                    >
                        <div className="flex flex-col p-6 gap-5">
                            {navLinks.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                                    className="text-sm uppercase tracking-[0.2em] text-brand-dark"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    openCalendly("30min", "mobile-book-call");
                                }}
                                data-testid="mobile-cta-button"
                                className="bg-brand-orange text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.2em] text-center mt-2 cursor-pointer"
                            >
                                Book a Call
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
